import React, { useEffect, useRef, useState } from 'react';
import styles from './index.module.css';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { showToast } from '../../../../components/ToastMsg';
import BeatLoader from 'react-spinners/BeatLoader';
import GridLoader from 'react-spinners/GridLoader';
import { getActiveCertificateTypes } from '../../../../redux/features/certificateType/certificateTypeService';
import { getCertificateTemplateById, updateCertificateTemplate } from '../../../../redux/features/certificateTemplate/certificateTemplateService';
import { getCertificateTemplate, STATUS } from '../../../../redux/features/certificateTemplate/certificateTemplateSlice';

const CertificateTemplateEdit = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();

  const { status: templateStatus, data: certificateTemplate } = useSelector(getCertificateTemplate);
  const isCertificateTemplateLoading = templateStatus === STATUS.LOADING;
  const isCertificateTemplateLoaded = templateStatus === STATUS.SUCCEEDED;
  const isCertificateTemplateFailed = templateStatus === STATUS.FAILED;
  const [selectedTemplate, setSelectedTemplate] = useState({});

  const [previews, setPreviews] = useState({
    template: null,
    header_logo: null,
    footor_logo: null,
    background_image: null,
  });

  const [errorMessages, setErrorMessages] = useState({
    name: '',
    sampleImage: '',
  });
  const [disabledBtn, setBtnDisable] = useState(false);
  const dropdownRef = useRef(null);
  const templateRef = useRef(null);
  const headerLogoRef = useRef(null);
  const footerLogoRef = useRef(null);
  const backgroundImageRef = useRef(null);

  const [initialTemplate, setInitialTemplate] = useState(null);

 useEffect(() => {
  if (id) {
    dispatch(getCertificateTemplateById({ id }));
  }
  return () => {
    // reset the detail slice when leaving edit page
    dispatch({
      type: 'certificateTemplate/certificateTemplateDetails/reset',
    });
  };
}, [dispatch, id]);

 useEffect(() => {
  if (!id) return; // skip if no edit ID

  if (isCertificateTemplateLoaded && certificateTemplate) {
    setSelectedTemplate(certificateTemplate);
    setInitialTemplate(certificateTemplate);
    setPreviews({
      template: certificateTemplate.template_url || null,
      header_logo: certificateTemplate.header_logo || null,
      footor_logo: certificateTemplate.footor_logo || null,
      background_image: certificateTemplate.background_image || null,
    });
  }

  if (isCertificateTemplateFailed) {
    navigate('/admin/certificate-template', { replace: true });
  }
}, [templateStatus, certificateTemplate, id]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setSelectedTemplate((prev) => ({ ...prev, [name]: value }));
    setErrorMessages((prev) => ({ ...prev, [name]: '' }));
  };

  const handleFileChange = (event, fieldName) => {
    if (event.target.files.length > 0) {
      const file = event.target.files[0];
      if (!validateFile(file, fieldName)) return;

      setSelectedTemplate((prev) => ({ ...prev, [fieldName]: file }));

      const fileURL = URL.createObjectURL(file);
      setPreviews((prev) => ({ ...prev, [fieldName]: fileURL }));
    }
  };

  const handleDrop = (event, fieldName) => {
    event.preventDefault();
    const file = event.dataTransfer.files[0];
    if (!validateFile(file, fieldName)) return;

    setSelectedTemplate((prev) => ({ ...prev, [fieldName]: file }));

    const fileURL = URL.createObjectURL(file);
    setPreviews((prev) => ({ ...prev, [fieldName]: fileURL }));
  };

  const validateFile = (selectedFile, fieldName) => {
    const allowedTypes = ['image/jpeg', 'image/png'];

    if (!allowedTypes.includes(selectedFile.type)) {
      setErrorMessages((prev) => ({
        ...prev,
        [fieldName]: 'Invalid file type. Allowed types: JPEG, PNG',
      }));

      setSelectedTemplate((prev) => ({ ...prev, [fieldName]: null }));
      setBtnDisable(true);
      return false;
    }

    setErrorMessages((prev) => ({ ...prev, [fieldName]: '' }));
    setBtnDisable(false);
    return true;
  };

  const isValidTemplate = () => {
    const errors = {};
    const trimmedName = selectedTemplate.name.trim();
    if (!trimmedName) {
      errors.name = 'Name is required.';
    }

    if (Object.keys(errors).length > 0) {
      setErrorMessages(errors);
      return false;
    } else {
      return true;
    }
  };

  function getChangedValues(selectedData) {
    const changedValues = {};
    if (!initialTemplate) {
      return {};
    }
    for (const key in selectedData) {
      if (
        key !== 'id' &&
        selectedData.hasOwnProperty(key) &&
        selectedData[key] !== initialTemplate[key]
      ) {
        changedValues[key] = selectedData[key];
      }

      if (key === 'id') {
        changedValues[key] = selectedData[key];
      }
    }
    return changedValues;
  }

  const updateTemplateDetails = async () => {
    if (!isValidTemplate()) {
      return;
    }
    if (
      selectedTemplate.sampleImage &&
      !validateFile(selectedTemplate.sampleImage, 'sampleImage')
    ) {
      return;
    }

    const changedValues = getChangedValues(selectedTemplate);
    const hasChanges = Object.keys(changedValues).some((key) => key !== 'id');

    if (!hasChanges) {
      showToast('error', 'There are no changes to update.');
    } else {
      setBtnDisable(true);
      try {
        const response = await dispatch(
          updateCertificateTemplate({ id, ...changedValues })
        ).unwrap();
        setBtnDisable(false);
        setInitialTemplate({ ...selectedTemplate });
        showToast('success', response.message);
        dropdownRef.current?.clearSelection();
        navigate('/admin/certificate-template');
      } catch (error) {
        setBtnDisable(false);
        showToast('error', error.message);
      }
    }
  };

  return (
    <div className={`card-section`}>
      <div className={`main-card`}>
        <h1 className={`form-title`}>Edit Template</h1>
        <div className={styles.formContainer}>
          {isCertificateTemplateLoading ? (
            <div className={`loader-style`}>
              <GridLoader color='var(--primary)' />
            </div>
          ) : (
            <form className={styles.coordinatorForm} noValidate>
              <div className={`form-input-row`}>
                <div className={`form-input-col`}>
                  <div className={`form-input-group`}>
                    <label htmlFor='full_name' className={`form-label`}>
                      Name<span className={`required-star`}>*</span>
                    </label>
                    <input
                      type='text'
                      id='name'
                      name='name'
                      value={selectedTemplate.name || ''}
                      onChange={handleInputChange}
                      placeholder='Enter Name'
                      className={`form-input-default`}
                    />
                    {errorMessages.name && (
                      <p className={`text-danger`}>{errorMessages.name}</p>
                    )}
                  </div>
                </div>
              </div>
              <div className={`form-input-row`}>
                <div className={`form-input-col`}>
                  <div className={`form-input-group`}>
                    <label htmlFor='full_name' className={`form-label`}>
                      Sample Image
                      <span className={`required-star`}>*</span>
                    </label>
                    <div
                      onDrop={(e) => handleDrop(e, 'template')}
                      onDragOver={(e) => e.preventDefault()}
                      onClick={() => templateRef.current.click()}
                      className={styles.dropInput}
                    >
                      <input
                        type="file"
                        ref={templateRef}
                        accept="image/*"
                        onChange={(e) => handleFileChange(e, 'template')}
                        style={{ display: 'none' }}
                      />
                      {previews.template ? (
                        <img src={previews.template} className={styles.thumbnailImg} />
                      ) : (
                        <p>Template Image</p>
                      )}
                    </div>
                    {errorMessages.sampleImage && (
                      <p className={`text-danger`}>
                        {errorMessages.sampleImage}
                      </p>
                    )}
                  </div>
                </div>
              </div>
              <div className={`form-input-row`}>
                <div className={`form-input-col`}>
                  <div className={`form-input-group`}>
                    <label htmlFor='full_name' className={`form-label`}>
                      Header Logo
                      <span className={`required-star`}>*</span>
                    </label>
                    <div
                      onDrop={(e) => handleDrop(e, 'header_logo')}
                      onDragOver={(e) => e.preventDefault()}
                      onClick={() => headerLogoRef.current.click()}
                      className={styles.dropInput}
                    >
                      <input
                        type="file"
                        ref={headerLogoRef}
                        accept="image/*"
                        onChange={(e) => handleFileChange(e, 'header_logo')}
                        style={{ display: 'none' }}
                      />
                      {previews.header_logo ? (
                        <img src={previews.header_logo} className={styles.thumbnailImg} />
                      ) : (
                        <p>Header Logo</p>
                      )}
                    </div>
                    {errorMessages.sampleImage && (
                      <p className={`text-danger`}>
                        {errorMessages.sampleImage}
                      </p>
                    )}
                  </div>
                </div>
              </div>
              <div className={`form-input-row`}>
                <div className={`form-input-col`}>
                  <div className={`form-input-group`}>
                    <label htmlFor='full_name' className={`form-label`}>
                      Footer Logo
                      <span className={`required-star`}>*</span>
                    </label><div
                      onDrop={(e) => handleDrop(e, 'footor_logo')}
                      onDragOver={(e) => e.preventDefault()}
                      onClick={() => footerLogoRef.current.click()}
                      className={styles.dropInput}
                    >
                      <input
                        type="file"
                        ref={footerLogoRef}
                        accept="image/*"
                        onChange={(e) => handleFileChange(e, 'footor_logo')}
                        style={{ display: 'none' }}
                      />
                      {previews.footor_logo ? (
                        <img src={previews.footor_logo} className={styles.thumbnailImg} />
                      ) : (
                        <p>Footer Logo</p>
                      )}
                    </div>

                    {errorMessages.sampleImage && (
                      <p className={`text-danger`}>
                        {errorMessages.sampleImage}
                      </p>
                    )}
                  </div>
                </div>
              </div>
              <div className={`form-input-row`}>
                <div className={`form-input-col`}>
                  <div className={`form-input-group`}>
                    <label htmlFor='full_name' className={`form-label`}>
                      Background Image
                      <span className={`required-star`}>*</span>
                    </label>
                    <div
                      onDrop={(e) => handleDrop(e, 'background_image')}
                      onDragOver={(e) => e.preventDefault()}
                      onClick={() => backgroundImageRef.current.click()}
                      className={styles.dropInput}
                    >
                      <input
                        type="file"
                        ref={backgroundImageRef}
                        accept="image/*"
                        onChange={(e) => handleFileChange(e, 'background_image')}
                        style={{ display: 'none' }}
                      />
                      {previews.background_image ? (
                        <img src={previews.background_image} className={styles.thumbnailImg} />
                      ) : (
                        <p>Background Image</p>
                      )}
                    </div>
                    {errorMessages.sampleImage && (
                      <p className={`text-danger`}>
                        {errorMessages.sampleImage}
                      </p>
                    )}
                  </div>
                </div>
              </div>
              <button
                className={`btn-primary`}
                type='button'
                disabled={disabledBtn}
                onClick={updateTemplateDetails}
              >
                {disabledBtn ? (
                  <BeatLoader color='#ffffff' />
                ) : (
                  `Save`
                )}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default CertificateTemplateEdit;
